import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface TrainerPricingModalProps {
  trainerName: string;
  initialHourlyRate?: number;
  initialVatRate?: number;
  initialGrossHourlyRate?: number;
  onSave: (data: { hourlyRate: number; vatRate: number; grossHourlyRate: number }) => void;
  onClose: () => void;
  isLoading?: boolean;
}

export function TrainerPricingModal({
  trainerName,
  initialHourlyRate = 0,
  initialVatRate = 23,
  initialGrossHourlyRate,
  onSave,
  onClose,
  isLoading = false,
}: TrainerPricingModalProps) {
  const { t } = useTranslation();
  const [hourlyRate, setHourlyRate] = useState(initialHourlyRate);
  const [vatRate, setVatRate] = useState(initialVatRate);
  const [grossHourlyRate, setGrossHourlyRate] = useState(initialGrossHourlyRate || 0);

  // Calculate gross from net when net changes
  useEffect(() => {
    const gross = hourlyRate * (1 + vatRate / 100);
    setGrossHourlyRate(Math.round(gross * 100) / 100);
  }, [hourlyRate, vatRate]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ hourlyRate, vatRate, grossHourlyRate });
  };

  const vatOptions = [0, 5, 8, 23];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-brand-primary-deep rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border-strong">
          <div>
            <h2 className="text-lg font-semibold text-white">
              {t('trainers.editPricing', 'Edytuj cennik')}
            </h2>
            <p className="text-sm text-white/70">{trainerName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Net Hourly Rate */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">
              {t('trainers.netHourlyRate', 'Stawka netto (PLN/h)')}
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={hourlyRate}
              onChange={(e) => setHourlyRate(parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary focus:outline-none focus:border-brand-primary"
            />
          </div>

          {/* VAT Rate */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">
              {t('trainers.vatRate', 'Stawka VAT')}
            </label>
            <div className="flex gap-2">
              {vatOptions.map((vat) => (
                <button
                  key={vat}
                  type="button"
                  onClick={() => setVatRate(vat)}
                  className={`flex-1 py-2 rounded-lg text-sm transition-colors ${
                    vatRate === vat
                      ? 'bg-brand-primary text-white'
                      : 'bg-surface-card text-ink-primary hover:bg-surface-card-hover'
                  }`}
                >
                  {vat}%
                </button>
              ))}
            </div>
          </div>

          {/* Gross Hourly Rate (calculated) */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">
              {t('trainers.grossHourlyRate', 'Stawka brutto (PLN/h)')}
            </label>
            <input
              type="text"
              value={grossHourlyRate.toFixed(2)}
              readOnly
              className="w-full px-3 py-2 bg-surface-app-elevated border border-border-strong rounded-lg text-ink-primary cursor-not-allowed"
            />
            <p className="text-xs text-white/60 mt-1">
              {t('trainers.grossCalculated', 'Wyliczone automatycznie na podstawie stawki netto i VAT')}
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 bg-surface-card text-ink-primary rounded-lg hover:bg-surface-card-hover transition-colors"
            >
              {t('common.cancel', 'Anuluj')}
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 py-2 px-4 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? t('common.saving', 'Zapisywanie...') : t('common.save', 'Zapisz')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
